/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import _ from "lodash";
import { useAppMenuContext } from "../../../../Basis/Components/AppMenuContext";
import IAppMenuContext from "../../../../Basis/Components/AppMenuContext/IAppMenuContext";
import { IAppRollenContext, useAppRollenContext } from "../../../../Basis/Components/AppRollenContext";
import {
    BarcodeReadIcon,
    BoxCircleCheckIcon,
    CashRegisterIcon,
    ChartGanttIcon,
    ChartSimpleIcon,
    CoinsIcon,
    CommentEditIcon,
    FileInvoiceIcon,
    GearIcon,
    GlobeIcon,
    HandHoldingDollarIcon,
    HouseLaptopIcon,
    NewsIcon,
    PeopleGroupIcon,
    PersonChalkboardIcon,
    QuestionIcon,
    RoadmapIcon,
    ShoppingBagIcon,
    SupportIcon,
    UsersBetweenLinesIcon,
    UsersClassIcon,
    UsersIcon
} from "../../../../Basis/FontAwesome";
import useErmittleBuchungsportalUrl, { ErmittleBuchungsportalUrlFunc } from "../../../../Basis/Utils/useErmittleBuchungsportalUrl";
import useNavigate, { NavigateFunc } from "../../../../Basis/Utils/useNavigate";
import WindowUtil from "../../../../Basis/Utils/WindowUtil";
import UIConstants from "../../../../UIConstants";
import { useAppHelpContext } from "../../../Support/Components/AppHelpContext";
import IAppHelpContext from "../../../Support/Components/AppHelpContext/IAppHelpContext";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nMandanten";
import Kachel from "./Kachel";
import { KachelColor, KachelIdentifier } from "./KachelInfo";

export default class StartseiteViewModel {
    private readonly _appHelpContext: IAppHelpContext;
    private readonly _appMenuContext: IAppMenuContext;
    private readonly _appRollenContext: IAppRollenContext;
    private readonly _navigate: NavigateFunc;
    private readonly _ermittleBuchungsportalUrl: ErmittleBuchungsportalUrlFunc;
    private readonly _t;

    public constructor() {
        this._appHelpContext = useAppHelpContext();
        this._appMenuContext = useAppMenuContext();
        this._appRollenContext = useAppRollenContext();
        this._t = useTranslation().t;
        this._navigate = useNavigate();
        this._ermittleBuchungsportalUrl = useErmittleBuchungsportalUrl();
    }

    public get bloecke() {
        return _.map(
            _.groupBy(this.getKacheln(), (k) => k.block),
            (kacheln) => kacheln
        );
    }

    // Später müssen hier die Kacheln anhand der KachelInfo des Servers ermittelt werden
    // (Kachel aus KachelInfos ermitteln)
    private getKacheln(): Kachel[] {
        return this.getKachelDefinitionen().filter(d => d.isVisible === undefined || d.isVisible);
    }

    private readonly navigiereZu = (path: string) => () => {
        this._navigate(path, { replace: false, enableChangeTracking: true });
    }

    private getKachelDefinitionen(): Kachel[] {
        return [
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.veranstaltungen),
                kachelIdentifier: KachelIdentifier.seminare,
                color: KachelColor.secondary,
                icon: <UsersIcon />,
                onClick: this.navigiereZu("/seminare")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.veranstaltungsreihen),
                kachelIdentifier: KachelIdentifier.seminarreihen,
                color: KachelColor.secondary,
                icon: <UsersBetweenLinesIcon />,
                onClick: this.navigiereZu("/seminarreihen"),
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.bestellungen),
                kachelIdentifier: KachelIdentifier.bestellungen,
                color: KachelColor.secondary,
                icon: <BoxCircleCheckIcon />,
                onClick: this.navigiereZu("/bestellungen")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.teilnahmen),
                kachelIdentifier: KachelIdentifier.teilnahmen,
                color: KachelColor.secondary,
                icon: <UsersClassIcon />,
                onClick: this.navigiereZu("/teilnahmen")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.adressen),
                kachelIdentifier: KachelIdentifier.adressen,
                color: KachelColor.secondary,
                icon: <PeopleGroupIcon />,
                onClick: this.navigiereZu("/adressen"),
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.dozierende),
                kachelIdentifier: KachelIdentifier.dozierende,
                color: KachelColor.secondary,
                icon: <PersonChalkboardIcon />,
                onClick: this.navigiereZu("/dozierende"),
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.artikel),
                kachelIdentifier: KachelIdentifier.artikel,
                color: KachelColor.secondary,
                icon: <ShoppingBagIcon />,
                onClick: this.navigiereZu("/artikelsammlung"),
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.bestellteArtikel),
                kachelIdentifier: KachelIdentifier.bestellterArtikel,
                color: KachelColor.secondary,
                icon: <CashRegisterIcon />,
                onClick: this.navigiereZu("/bestellteArtikel"),
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.belege),
                kachelIdentifier: KachelIdentifier.belege,
                color: KachelColor.secondary,
                icon: <FileInvoiceIcon />,
                onClick: this.navigiereZu("/belege")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.belegungen),
                kachelIdentifier: KachelIdentifier.belegungen,
                color: KachelColor.secondary,
                icon: <HouseLaptopIcon />,
                onClick: this.navigiereZu("/belegungen")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.zahlungen),
                kachelIdentifier: KachelIdentifier.zahlungen,
                color: KachelColor.secondary,
                icon: <CoinsIcon />,
                onClick: this.navigiereZu("/zahlungen")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.fibuExporte),
                kachelIdentifier: KachelIdentifier.fibuExporte,
                color: KachelColor.tertiary,
                icon: <HandHoldingDollarIcon />,
                onClick: this.navigiereZu("/fibuExporte"),
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.auswertungen),
                kachelIdentifier: KachelIdentifier.auswertungen,
                color: KachelColor.tertiary,
                icon: <ChartSimpleIcon />,
                onClick: this.navigiereZu("/auswertungen")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.ressourcenplan),
                kachelIdentifier: KachelIdentifier.ressourcenplan,
                color: KachelColor.tertiary,
                icon: <ChartGanttIcon />,
                onClick: this.navigiereZu("/ressourcenplan")
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.grundeinstellungen),
                kachelIdentifier: KachelIdentifier.grundeinstellungen,
                color: KachelColor.tertiary,
                icon: <GearIcon />,
                onClick: () => this._appMenuContext.zeigeGrundeinstellungenMenu(),
                isVisible: this._appRollenContext.hatGrundeinstellungenRecht,
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.buchungsportal),
                kachelIdentifier: KachelIdentifier.buchungsportal,
                color: KachelColor.tertiary,
                icon: <GlobeIcon />,
                onClick: () => WindowUtil.oeffneInTab(this._ermittleBuchungsportalUrl())
            },
            {
                block: 0,
                caption: this._t(translationKeys.startseiteView.ticketscanner),
                kachelIdentifier: KachelIdentifier.ticketscanner,
                color: KachelColor.tertiary,
                icon: <BarcodeReadIcon />,
                onClick: this.navigiereZu("/ticketscanner")
            },
            {
                block: 1,
                caption: this._t(translationKeys.startseiteView.faq),
                kachelIdentifier: KachelIdentifier.faq,
                color: KachelColor.secondary,
                icon: <QuestionIcon />,
                onClick: () => WindowUtil.oeffneInTab(UIConstants.faqUrl)
            },
            {
                block: 1,
                caption: this._t(translationKeys.startseiteView.support),
                kachelIdentifier: KachelIdentifier.support,
                color: KachelColor.secondary,
                icon: <SupportIcon />,
                onClick: () => this._appHelpContext.setIsKontaktformularDialogOpen(true)
            },
            {
                block: 1,
                caption: this._t(translationKeys.startseiteView.verbesserungsvorschlag),
                kachelIdentifier: KachelIdentifier.verbesserungsvorschlag,
                color: KachelColor.secondary,
                icon: <CommentEditIcon />,
                onClick: this.navigiereZu("/verbesserungsvorschlaege")
            },
            {
                block: 1,
                caption: this._t(translationKeys.startseiteView.roadmap),
                kachelIdentifier: KachelIdentifier.roadmap,
                color: KachelColor.secondary,
                icon: <RoadmapIcon />,
                onClick: this.navigiereZu("/roadmap")
            },
            {
                block: 1,
                caption: this._t(translationKeys.startseiteView.neuigkeiten),
                kachelIdentifier: KachelIdentifier.neuigkeiten,
                color: KachelColor.secondary,
                icon: <NewsIcon />,
                onClick: () => WindowUtil.oeffneInTab(UIConstants.blogUrl)
            },
        ];
    }
}